import { Box, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCustomerGroups, getProductGroups, getCGHistory, GetCGModificationDetails, setState, approveCG } from "./cgpgSlice";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import EditIcon from '@mui/icons-material/Edit';
import { setCGState as setCustomerGroupState, getCGDetail, getCGRefData, getCGForCountry } from "../../Setup/Customer/customerGroupSetupSlice";
import muiConfig from '../../../assets/MuiConfig.json'
import { getCGHistoryRows, getLatestHistoryRowId, getClickedGridCellField, isCgpgGridActionField, getCgpgDetailPanelGridSx } from "./cgpgUtils";

const DetailPanelContext = createContext({ collapseDetailPanel: () => {} });

const CHECKBOX_COLUMN_WIDTH = 52;
const ACTION_COLUMN_WIDTH = 210;
const EDIT_COLUMN_WIDTH = 200;
const DETAIL_PANEL_TOGGLE_FIELD = "__detail_panel_toggle__";
const CUSTOMER_GROUP_DETAIL_FIELDS = new Set([
   "customerGroupName",
   "countryCode",
   "status",
   "updatedDate",
   "updatedBy",
   "reject",
   "approve",
   "edit",
]);

function getDetailPanelMetrics(columns) {
   const detailPanelWidth = columns
      .filter((column) => CUSTOMER_GROUP_DETAIL_FIELDS.has(column.field))
      .reduce((sum, column) => sum + column.width, 0);

   return {
      detailPanelInset: CHECKBOX_COLUMN_WIDTH,
      detailPanelWidth,
   };
}

//#region Create Columns for Customer Group Table
function getCustomerGroupsCol() {
   const width = 200;
   return [
      { field: "customerGroupName", headerName: "Group Name", width: width },
      { field: "countryCode", headerName: "Country Code", width: width },
      { field: "status", headerName: "Status", width: width },
      { field: "updatedDate", headerName: "Last Action Date", width: width, valueFormatter: (params) =>moment(params.value).format('DD/MM/YYYY') },
      { field: "updatedBy", headerName: "Last Action By", width: width },
      {
			field: "reject",
			headerName: "Reject Customer Group",
			width: 210,
         align:"center",
			renderCell: RejectButton,
		},
		{
			field: "approve",
			headerName: "Approve Customer Group",
			width: 210,
         align:"center",
			renderCell: ApproveButton,
		},
      {
         field: "edit",
         headerName: "Edit",
         width: width,
         renderCell: EditButton,
         align:"center",
			headerAlign:"center",
      },
   ];
}
//#endregion

// #region Customer Group Table Code
export default function CustomerGroupsTable() {
	const dispatch = useDispatch();
	const states = useSelector((store) => store.cgpgApproval);
   const [isAll, setIsAll] = useState(false);
   const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState([]);
	const { customerGroupsTable, selectedRows, customerGroups, cgHistory, selectedCustomerGroupHistoryID, isLoading, detailPanelCollapseToken } = states;
	const { rows } = customerGroupsTable;
   const columns = getCustomerGroupsCol();
   const { detailPanelInset, detailPanelWidth } = getDetailPanelMetrics(columns);

   const [paginationModel, setPaginationModel] = useState({
      pageSize: 20,
      page: 0
   });

   const loadCustomerGroupDetails = (customerGroupID, customerGroupModificationID) => {
      dispatch(getCGHistory({ customerGroupID }))
         .unwrap()
         .then(() => {
            dispatch(
               GetCGModificationDetails({
                  customerGroupID,
                  customerGroupModificationID,
               })
            );
         })
         .catch(() => {});
   };

   const updateDetailPanel = (expandedRowIds) => {
      setDetailPanelExpandedRowIds(expandedRowIds);

      if (expandedRowIds.length === 0) {
         dispatch(setState("selectedCustomerGroupHistoryID", null));
         dispatch(setState("cgHistory", null));
         dispatch(setState("cG_Details", null));
         dispatch(setState("cgModificationDetails", null));
         return;
      }

      const customerGroupID = expandedRowIds[expandedRowIds.length - 1];
      const clickedCustomerGroup = customerGroups.find((cg) => cg.customerGroupID === customerGroupID);

      dispatch(setState("selectedCustomerGroupHistoryID", customerGroupID));
      dispatch(setState("clickedRowComment", clickedCustomerGroup?.comments));
      dispatch(setState("groupRowClicked", true));
      loadCustomerGroupDetails(customerGroupID, 0);
   };

   const collapseDetailPanel = () => {
      updateDetailPanel([]);
   };

   useEffect(() => {
      if (detailPanelCollapseToken === 0) {
         return;
      }
      collapseDetailPanel();
   }, [detailPanelCollapseToken]);

   const handleRowClick = (rowData, event) => {
      if (rowData == undefined) {
         return;
      }

      const clickedField = getClickedGridCellField(event);
      if (isCgpgGridActionField(clickedField)) {
         collapseDetailPanel();
         return;
      }

      const clickedCustomerGroupID = rowData?.row?.id;
      const clickedCustomerGroup = customerGroups.find((cg) => cg.customerGroupID === clickedCustomerGroupID);
      const isExpanded = detailPanelExpandedRowIds.includes(clickedCustomerGroupID);

      dispatch(setState("clickedRowComment", clickedCustomerGroup?.comments));
      dispatch(setState("groupRowClicked", true));
      updateDetailPanel(isExpanded ? [] : [clickedCustomerGroupID]);
   };

   const handleCellClick = (params, event) => {
      if (params.field === "reject" || params.field === "approve" || params.field === "edit") {
         event.stopPropagation();
      }
   };

   const getDetailPanelContent = ({ row }) => {
      if (row.id !== selectedCustomerGroupHistoryID) {
         return null;
      }

      if (isLoading && cgHistory === null) {
         return (
            <Box
               sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 2,
                  width: detailPanelWidth,
               }}
            >
               <CircularProgress size={24} />
            </Box>
         );
      }

      return <CGHistoryDetail detailPanelWidth={detailPanelWidth} />;
   };

   const setModal = (newPaginationModel) => {
      if(newPaginationModel.pageSize === muiConfig.DataGrid_All && newPaginationModel.page === 0 && rows?.length > 100){
      setIsAll(true);
      }else{
      setIsAll(false);
      }
      setPaginationModel(newPaginationModel);
   }

	return (
		<DetailPanelContext.Provider value={{ collapseDetailPanel }}>
		<Box sx={{ mt: 3 }}>
			<DataGridPro
            sx={{
               "& .MuiDataGrid-columnHeaders":{
                  backgroundColor:"grey.lighter"
               },
               [`& .MuiDataGrid-columnHeader[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"], & .MuiDataGrid-cell[data-field="${DETAIL_PANEL_TOGGLE_FIELD}"]`]: {
                  display: "none",
                  width: "0 !important",
                  minWidth: "0 !important",
                  maxWidth: "0 !important",
                  padding: "0 !important",
                  border: "none",
               },
               ...getCgpgDetailPanelGridSx(detailPanelInset, detailPanelWidth),
               ...(!isAll ? {
                  "& .MuiDataGrid-virtualScroller": {
                     maxHeight: "unset !important"
                  }
                } : {
                  "& .MuiDataGrid-virtualScroller": {
                     maxHeight: "80vh !important"
                  }
               })
            }}
            slots={{
               detailPanelCollapseIcon: () => null,
               detailPanelExpandIcon: () => null,
            }}
				rows={rows}
				columns={columns}
				onRowSelectionModelChange={(selRows) => {
               dispatch(setState("selectedRows", selRows));
				}}
            rowSelectionModel={selectedRows}
            isRowSelectable={(params) => params.row.canApproveReject === true}
            checkboxSelection
            disableRowSelectionOnClick
            disableColumnMenu
            initialState={{
					pagination: { paginationModel: { pageSize:20, page:0 }}
				}}
				pagination
				pageSizeOptions={[20, 50, 100, { value: muiConfig.DataGrid_All , label: 'All' }]}
            paginationModel={paginationModel}
            onPaginationModelChange = {setModal}
            getRowClassName={(params) =>
					params.row.canApproveReject !== true ? 'disabled-row' : ''
				}
            onRowClick={handleRowClick}
            onCellClick={handleCellClick}
            detailPanelExpandedRowIds={detailPanelExpandedRowIds}
            onDetailPanelExpandedRowIdsChange={updateDetailPanel}
            getDetailPanelHeight={() => "auto"}
            getDetailPanelContent={getDetailPanelContent}
			/>
		</Box>
		</DetailPanelContext.Provider>
	);
}
//#endregion

// #region Reject Button Code
function RejectButton(params) {
   const { collapseDetailPanel } = useContext(DetailPanelContext);
   const autherization = localStorage.getItem("data");
	const data = JSON.parse(autherization);
	let setAccess = true;
	if (data?.restrictedAccess == 0) setAccess = true;
	else setAccess = false;
	const isRestrictedAccess = setAccess;
	const dispatch = useDispatch();
   const states = useSelector((store) => store.cgpgApproval);
   const {
      customerGroups,
      selectedCountryID,
      selectedGroupStatus,
      selectedPeriod,
      selectedView,
   } = states;

   function search() {
      dispatch(setState("customerGroups", null));
      dispatch(setState("productGroups", null));
      dispatch(setState("selectedRows", []));

      const obj = {
         logedUser: JSON.parse(localStorage.getItem("data")),
         countryId: selectedCountryID,
         groupStatus: selectedGroupStatus,
         period: selectedPeriod,
         searchText: "",
      };

      if (selectedView === "Product Group") {
         dispatch(getProductGroups(obj));
      } else {
         dispatch(getCustomerGroups(obj));
      }
   }
   function openRejectModal() {
      collapseDetailPanel();
      let id = params.row.id;
      let customerGroup = customerGroups.find((pg) => pg.customerGroupID === id);
      dispatch(setState("selectedCustomerGroupName", customerGroup.customerGroupName));
      dispatch(setState("selectedCustomerGroupID",id));
      dispatch(setState("rejectSingleModalOpen", true));
	}


   if (params.row.canApproveReject && isRestrictedAccess) {
      return (
         <IconButton onClick={openRejectModal}>
            <CancelIcon sx={{ color: "red.main" }} />
         </IconButton>
      );
   }
   return null;
}
// #endregion

// #region Approve Button Code
function ApproveButton(params) {
   const { collapseDetailPanel } = useContext(DetailPanelContext);
   const autherization = localStorage.getItem("data");
	const data = JSON.parse(autherization);
	let setAccess = true;
	if (data?.restrictedAccess == 0) setAccess = true;
	else setAccess = false;
	const isRestrictedAccess = setAccess;

	const dispatch = useDispatch();
   const states = useSelector((store) => store.cgpgApproval);
   const {
      customerGroups,
      selectedCountryID,
      selectedGroupStatus,
      selectedPeriod,
      selectedView,
      searchText
   } = states;

   function search() {
      dispatch(setState("customerGroups", null));
      dispatch(setState("productGroups", null));
      dispatch(setState("selectedRows", []));

      const obj = {
         logedUser: JSON.parse(localStorage.getItem("data")),
         countryId: selectedCountryID,
         groupStatus: selectedGroupStatus,
         period: selectedPeriod,
         searchText: searchText,
      };

      if (selectedView === "Product Group") {
         dispatch(getProductGroups(obj));
      } else {
         dispatch(getCustomerGroups(obj));
      }
   }

   function approve() {
      collapseDetailPanel();
      const id = params.row.id;
      const customerGroup = customerGroups.find((cg) => cg.customerGroupID === id);
      const logedUser = JSON.parse(localStorage.getItem("data"));
      const obj = {
         ...customerGroup,
         isApprove: 1,
         userRole:logedUser.roleName
      };
      console.log("Approval_CGPG_ApproveCG", obj);
      dispatch(approveCG(obj)).then(() => {
            search();
		});
   }

   if (params.row.canApproveReject && isRestrictedAccess) {
      return (
         <IconButton onClick={approve}>
            <CheckCircleIcon sx={{ color: "green.main" }} />
         </IconButton>
      );
   }
   return null;
}
// #endregion

// #region Edit Button
function EditButton(params) {
   const autherization = localStorage.getItem("data");
	const data = JSON.parse(autherization);
	let setAccess = true;
	if (data?.restrictedAccess == 0) setAccess = true;
	else setAccess = false;
	const isRestrictedAccess = setAccess;
	const navigate = useNavigate();
	const dispatch = useDispatch();

   const customerGroupApprovalState = useSelector((store) => store.cgpgApproval);
   const {customerGroups,countries}=customerGroupApprovalState;

   function gotoEditCustomerGroup() {
      const id = params.row.id;
      const customerGroup = customerGroups.find((cg) => cg.customerGroupID === id);
      if (!customerGroup) {
         return;
      }

      const countryRow = countries.find(
         (country) => String(country.countryID) === String(customerGroup.countryID)
      );
      if (!countryRow) {
         return;
      }

      const typeCode =
         customerGroup.customerGroupTypeCode ??
         customerGroup.CustomerGroupTypeCode ??
         "";
      const countryID = countryRow.countryID;

      dispatch(setCustomerGroupState("isEdit", true));
      dispatch(setCustomerGroupState("country", countryRow.countryName));
      dispatch(setCustomerGroupState("countryID", countryID));
      dispatch(
         setCustomerGroupState("customerGroupName", customerGroup.customerGroupName ?? "")
      );
      dispatch(setCustomerGroupState("customerGroupID", customerGroup.customerGroupID));
      dispatch(setCustomerGroupState("customerTypeCode", typeCode));
      dispatch(setCustomerGroupState("isNavigate", true));

      dispatch(getCGRefData({ countryID }))
         .unwrap()
         .then(async (refData) => {
            const detail = await dispatch(
               getCGDetail({ customerGroupID: customerGroup.customerGroupID })
            ).unwrap();
            const typeForSearch = typeCode || detail?.customerGroupTypeCode || "";
            const typeRow = refData?.customerTypes?.find(
               (t) => String(t.customerTypeCode) === String(typeForSearch)
            );
            if (typeRow) {
               dispatch(setCustomerGroupState("customerType", typeRow.customerTypeName));
               dispatch(setCustomerGroupState("customerTypeCode", typeForSearch));
            }
            if (typeForSearch) {
               await dispatch(
                  getCGForCountry({
                     countryID,
                     customerTypeCode: typeForSearch,
                     customerGroupName: customerGroup.customerGroupName ?? "",
                  })
               ).unwrap();
            }
            navigate("/setup/customer-group");
         })
         .catch(() => {
            navigate("/setup/customer-group");
         });
   }
   if (params.row.canEdit && isRestrictedAccess) {
   return (
      <IconButton
      sx={{
         padding:0,
         '&:hover':{
                  backgroundColor:'#F8E9E7',
                  borderRadius:'2px',
               }
      }}>
         <EditIcon onClick={gotoEditCustomerGroup}
             sx={{
               color: "#000000",
               fontSize:'large',
               justifyContent: 'center',
               borderRadius:'2px',
               width:25,
               height:22,
               backgroundColor: 'transparent',
               }} />
      </IconButton>
   );
}
}

function CGHistoryDetail({ detailPanelWidth }) {
   const dispatch = useDispatch();
   const { cgHistory, selectedCustomerGroupHistoryID } = useSelector((store) => store.cgpgApproval);
   const historyRows = getCGHistoryRows(cgHistory);
   const latestHistoryRowId = getLatestHistoryRowId(historyRows);

   const handleHistoryRowClick = (item) => {
      const customerGroupID = item.customerGroupID ?? selectedCustomerGroupHistoryID;
      const customerGroupModificationID = item.customerGroupModificationID;

      if (!customerGroupID || customerGroupModificationID == null) {
         return;
      }

      dispatch(setState("clickedRowComment", item.comments ?? ""));
      dispatch(setState("groupRowClicked", true));
      dispatch(
         GetCGModificationDetails({
            customerGroupID,
            customerGroupModificationID,
         })
      );
   };

   if (historyRows.length === 0) {
      return (
         <Box sx={{ py: 2, px: 2, width: detailPanelWidth }}>
            No history found.
         </Box>
      );
   }

   return (
      <TableContainer
         component={Paper}
         elevation={0}
         sx={{ width: detailPanelWidth, overflowX: "hidden" }}
      >
         <Table size="small" sx={{ width: "100%" }}>
            <TableHead
               sx={{
                  "& th": {
                     backgroundColor: "#EEF2F4!important",
                     fontSize: "12px",
                  },
               }}
            >
               <TableRow>
                  <TableCell>Submission No</TableCell>
                  <TableCell>Submission Status</TableCell>
                  <TableCell>Last Action Date</TableCell>
                  <TableCell>Last Action By</TableCell>
                  <TableCell align="center" sx={{ width: ACTION_COLUMN_WIDTH }}>
                     Reject Customer Group
                  </TableCell>
                  <TableCell align="center" sx={{ width: ACTION_COLUMN_WIDTH }}>
                     Approve Customer Group
                  </TableCell>
                  <TableCell align="center" sx={{ width: EDIT_COLUMN_WIDTH }}>
                     Edit
                  </TableCell>
               </TableRow>
            </TableHead>
            <TableBody
               sx={{
                  "& tr": {
                     backgroundColor: "rgb(249, 251, 254)",
                     fontSize: "12px",
                  },
               }}
            >
               {historyRows.map((item) => (
                  <TableRow
                     key={item.id}
                     hover
                     sx={{ cursor: "pointer" }}
                     onClick={() => handleHistoryRowClick(item)}
                  >
                     <TableCell>
                        {item.submissionNumber}
                     </TableCell>
                     <TableCell>
                        {item.status}
                     </TableCell>
                     <TableCell>
                        {item.modifiedDate && moment(item.modifiedDate).format("DD/MM/YYYY")}
                     </TableCell>
                     <TableCell>
                        {item.modifiedBy}
                     </TableCell>
                     <TableCell
                        align="center"
                        sx={{ width: ACTION_COLUMN_WIDTH }}
                        onClick={(event) => event.stopPropagation()}
                     >
                        <CGHistoryRejectButton
                           row={item}
                           customerGroupID={selectedCustomerGroupHistoryID}
                        />
                     </TableCell>
                     <TableCell
                        align="center"
                        sx={{ width: ACTION_COLUMN_WIDTH }}
                        onClick={(event) => event.stopPropagation()}
                     >
                        <CGHistoryApproveButton
                           row={item}
                           customerGroupID={selectedCustomerGroupHistoryID}
                        />
                     </TableCell>
                     <TableCell
                        align="center"
                        sx={{ width: EDIT_COLUMN_WIDTH }}
                        onClick={(event) => event.stopPropagation()}
                     >
                        <CGHistoryEditButton
                           row={item}
                           customerGroupID={selectedCustomerGroupHistoryID}
                           isLatestSubmission={item.id === latestHistoryRowId}
                        />
                     </TableCell>
                  </TableRow>
               ))}
            </TableBody>
         </Table>
      </TableContainer>
   );
}

function CGHistoryRejectButton({ row, customerGroupID }) {
   const { collapseDetailPanel } = useContext(DetailPanelContext);
   const autherization = localStorage.getItem("data");
   const data = JSON.parse(autherization);
   let setAccess = true;
   if (data?.restrictedAccess == 0) setAccess = true;
   else setAccess = false;
   const isRestrictedAccess = setAccess;

   const dispatch = useDispatch();
   const states = useSelector((store) => store.cgpgApproval);
   const { customerGroups } = states;

   function openRejectModal() {
      collapseDetailPanel();
      const customerGroup = customerGroups.find((cg) => cg.customerGroupID === customerGroupID);

      dispatch(setState("selectedCustomerGroupName", customerGroup?.customerGroupName ?? ""));
      dispatch(setState("selectedCustomerGroupID", customerGroupID));
      dispatch(setState("rejectSingleModalOpen", true));
   }

   if (row.canApproveReject && isRestrictedAccess) {
      return (
         <IconButton onClick={openRejectModal}>
            <CancelIcon sx={{ color: "red.main" }} />
         </IconButton>
      );
   }

   return null;
}

function CGHistoryApproveButton({ row, customerGroupID }) {
   const { collapseDetailPanel } = useContext(DetailPanelContext);
   const autherization = localStorage.getItem("data");
   const data = JSON.parse(autherization);
   let setAccess = true;
   if (data?.restrictedAccess == 0) setAccess = true;
   else setAccess = false;
   const isRestrictedAccess = setAccess;

   const dispatch = useDispatch();
   const states = useSelector((store) => store.cgpgApproval);
   const {
      customerGroups,
      selectedCountryID,
      selectedGroupStatus,
      selectedPeriod,
      selectedView,
      searchText,
   } = states;

   function search() {
      dispatch(setState("customerGroups", null));
      dispatch(setState("productGroups", null));
      dispatch(setState("selectedRows", []));

      const obj = {
         logedUser: JSON.parse(localStorage.getItem("data")),
         countryId: selectedCountryID,
         groupStatus: selectedGroupStatus,
         period: selectedPeriod,
         searchText: searchText,
      };

      if (selectedView === "Product Group") {
         dispatch(getProductGroups(obj));
      } else {
         dispatch(getCustomerGroups(obj));
      }
   }

   function approve() {
      collapseDetailPanel();
      const customerGroup = customerGroups.find((cg) => cg.customerGroupID === customerGroupID);
      const logedUser = JSON.parse(localStorage.getItem("data"));
      const obj = {
         ...customerGroup,
         isApprove: 1,
         userRole: logedUser.roleName,
      };

      dispatch(approveCG(obj)).then(() => {
         search();
      });
   }

   if (row.canApproveReject && isRestrictedAccess) {
      return (
         <IconButton onClick={approve}>
            <CheckCircleIcon sx={{ color: "green.main" }} />
         </IconButton>
      );
   }

   return null;
}

function CGHistoryEditButton({ row, customerGroupID, isLatestSubmission }) {
   const autherization = localStorage.getItem("data");
   const data = JSON.parse(autherization);
   let setAccess = true;
   if (data?.restrictedAccess == 0) setAccess = true;
   else setAccess = false;
   const isRestrictedAccess = setAccess;
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const { customerGroups, countries } = useSelector((store) => store.cgpgApproval);

   function gotoEditCustomerGroup() {
      const customerGroup = customerGroups.find((cg) => cg.customerGroupID === customerGroupID);

      if (!customerGroup) {
         return;
      }

      const countryRow = countries.find(
         (country) => String(country.countryID) === String(customerGroup.countryID)
      );
      if (!countryRow) {
         return;
      }

      const typeCode =
         customerGroup.customerGroupTypeCode ??
         customerGroup.CustomerGroupTypeCode ??
         "";
      const cid = countryRow.countryID;
      const { customerGroupID: selectedCustomerGroupID } = customerGroup;

      dispatch(setCustomerGroupState("isEdit", true));
      dispatch(setCustomerGroupState("country", countryRow.countryName));
      dispatch(setCustomerGroupState("countryID", cid));
      dispatch(
         setCustomerGroupState("customerGroupName", customerGroup.customerGroupName ?? "")
      );
      dispatch(setCustomerGroupState("customerGroupID", selectedCustomerGroupID));
      dispatch(setCustomerGroupState("customerTypeCode", typeCode));
      dispatch(setCustomerGroupState("isNavigate", true));

      dispatch(getCGRefData({ countryID: cid }))
         .unwrap()
         .then(async (refData) => {
            const detail = await dispatch(
               getCGDetail({ customerGroupID: selectedCustomerGroupID })
            ).unwrap();
            const typeForSearch = typeCode || detail?.customerGroupTypeCode || "";
            const typeRow = refData?.customerTypes?.find(
               (t) => String(t.customerTypeCode) === String(typeForSearch)
            );
            if (typeRow) {
               dispatch(setCustomerGroupState("customerType", typeRow.customerTypeName));
               dispatch(setCustomerGroupState("customerTypeCode", typeForSearch));
            }
            if (typeForSearch) {
               await dispatch(
                  getCGForCountry({
                     countryID: cid,
                     customerTypeCode: typeForSearch,
                     customerGroupName: customerGroup.customerGroupName ?? "",
                  })
               ).unwrap();
            }
            navigate("/setup/customer-group");
         })
         .catch(() => {
            navigate("/setup/customer-group");
         });
   }

   if (row.canEdit && isRestrictedAccess && isLatestSubmission) {
      return (
         <IconButton
            sx={{
               padding: 0,
               "&:hover": {
                  backgroundColor: "#F8E9E7",
                  borderRadius: "2px",
               },
            }}
         >
            <EditIcon
               onClick={gotoEditCustomerGroup}
               sx={{
                  color: "#000000",
                  fontSize: "large",
                  justifyContent: "center",
                  borderRadius: "2px",
                  width: 25,
                  height: 22,
                  backgroundColor: "transparent",
               }}
            />
         </IconButton>
      );
   }

   return null;
}

// #endregion
